/**
 * PhoneApps — Shared app catalogue used by PhoneScreen and ComputerScreen.
 *
 * Each app exposes:
 *   id, name, iconText, iconBg, bg, render(ctx), onMount?(root, ctx)
 *
 * ctx = { world, player } — apps may read live game state (e.g. Waze map).
 */

const APP_TEAMS = {
  id: 'teams',
  name: 'Teams',
  iconText: 'T',
  iconBg: 'linear-gradient(135deg, #6264A7 0%, #4B4D8A 100%)',
  bg: '#F3F2F1',
  render() {
    const chats = [
      { name: 'Edu (Papá)', last: 'te amo gordo', time: '9:32', unread: 1, color: '#5B9BD5' },
      { name: 'Mamá', last: '¿Almorzaste?', time: '9:01', unread: 2, color: '#E08B8B' },
      { name: 'Equipo MundoFede', last: 'Steve: subí un creeper', time: 'ayer', unread: 0, color: '#7AB97A' },
      { name: 'Tía Caro', last: '🎂🎂🎂', time: 'ayer', unread: 0, color: '#C778C2' },
      { name: 'Clase 3º básico', last: 'Profe: tarea de mate', time: 'lun', unread: 0, color: '#E8A86B' },
    ];

    const items = chats.map((c) => `
      <div style="
        display: flex; align-items: center;
        padding: 12px 16px;
        border-bottom: 1px solid #E1DFDD;
        cursor: pointer;
      ">
        <div style="
          width: 44px; height: 44px;
          border-radius: 50%; background: ${c.color};
          color: white; display: flex; align-items: center; justify-content: center;
          font-weight: 600; font-size: 16px;
          flex-shrink: 0;
        ">${c.name.charAt(0)}</div>
        <div style="flex: 1; margin-left: 12px; min-width: 0;">
          <div style="display:flex; justify-content:space-between; align-items:center;">
            <div style="font-weight:600; color:#252423; font-size:14px;">${c.name}</div>
            <div style="font-size:11px; color:#8A8886;">${c.time}</div>
          </div>
          <div style="
            font-size:13px; color:#605E5C;
            white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
            margin-top:2px;
          ">${c.last}</div>
        </div>
        ${c.unread > 0 ? `
          <div style="
            margin-left: 8px;
            background:#6264A7; color:white;
            min-width:20px; height:20px;
            border-radius:10px;
            font-size:11px; font-weight:600;
            display:flex; align-items:center; justify-content:center;
            padding: 0 6px;
          ">${c.unread}</div>` : ''}
      </div>
    `).join('');

    return `
      <div style="background:#464775; color:white; padding:12px 16px; font-weight:600;">
        Teams · Chats
      </div>
      ${items}
      <div style="padding:24px 16px; text-align:center; color:#8A8886; font-size:12px;">
        No hay más conversaciones
      </div>
    `;
  },
};

const APP_WAZE = {
  id: 'waze',
  name: 'Waze',
  iconText: 'W',
  iconBg: 'linear-gradient(135deg, #33CCFF 0%, #0099CC 100%)',
  bg: '#1E3A5F',
  render() {
    return `
      <div style="background:#33CCFF; color:white; padding:12px 16px; font-weight:600;">
        Waze · Mundo
      </div>
      <div style="padding: 16px; text-align: center; color: white;">
        <canvas id="waze-canvas" width="300" height="380"
          style="border-radius:12px; background:#243B55; display:block; margin:0 auto;">
        </canvas>
        <div style="margin-top:12px; font-size:13px;">
          <div id="waze-coords" style="opacity:0.85;">Cargando posición...</div>
          <div style="margin-top:6px; opacity:0.7; font-size:11px;">
            Tu auto · 0 km/h
          </div>
        </div>
      </div>
    `;
  },
  onMount(root, ctx) {
    const canvas = root.querySelector('#waze-canvas');
    const coords = root.querySelector('#waze-coords');
    if (!canvas || !ctx.world || !ctx.player) return;
    const c = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    const px = ctx.player.position.x;
    const pz = ctx.player.position.z;
    const range = 80; // blocks shown

    // Background
    c.fillStyle = '#243B55';
    c.fillRect(0, 0, W, H);

    // Sample world heights for a heightmap-style minimap
    for (let i = 0; i < W; i += 2) {
      for (let j = 0; j < H; j += 2) {
        const wx = Math.floor(px + (i / W - 0.5) * range * 2);
        const wz = Math.floor(pz + (j / H - 0.5) * range * 2);
        const h = ctx.world.getTopBlock ? ctx.world.getTopBlock(wx, wz) : 64;
        const shade = Math.min(255, Math.max(60, h * 3));
        // Greenish for land, blueish below sea level
        if (h < 60) c.fillStyle = `rgb(40, 60, ${shade + 40})`;
        else c.fillStyle = `rgb(${shade * 0.4}, ${shade * 0.7}, ${shade * 0.4})`;
        c.fillRect(i, j, 2, 2);
      }
    }

    // Player dot (center)
    c.fillStyle = '#33CCFF';
    c.beginPath();
    c.arc(W / 2, H / 2, 6, 0, Math.PI * 2);
    c.fill();
    c.strokeStyle = 'white';
    c.lineWidth = 2;
    c.stroke();

    // Markers for villas (if available)
    const drawMarker = (wx, wz, color, label) => {
      const sx = (wx - px) / (range * 2) * W + W / 2;
      const sy = (wz - pz) / (range * 2) * H + H / 2;
      if (sx < 0 || sx > W || sy < 0 || sy > H) return;
      c.fillStyle = color;
      c.fillRect(sx - 4, sy - 4, 8, 8);
      c.fillStyle = 'white';
      c.font = '10px sans-serif';
      c.fillText(label, sx + 6, sy + 3);
    };
    // Try common village positions (won't crash if undefined)
    if (ctx.world._villaMiseriaPos) drawMarker(ctx.world._villaMiseriaPos.x, ctx.world._villaMiseriaPos.z, '#FF453A', 'VM');
    if (ctx.world._villaProsperidadPos) drawMarker(ctx.world._villaProsperidadPos.x, ctx.world._villaProsperidadPos.z, '#FFD700', 'VP');

    coords.textContent = `Posición: X ${Math.floor(px)}, Z ${Math.floor(pz)}`;
  },
};

const APP_MAIL = {
  id: 'mail',
  name: 'Mail',
  iconText: '✉',
  iconBg: 'linear-gradient(135deg, #4D9EFF 0%, #0066CC 100%)',
  bg: '#FFFFFF',
  render() {
    const mails = [
      { from: 'Steve', subject: 'Tengo diamantes', preview: 'Encontré una veta enorme cerca del spawn...', unread: true, time: '9:14' },
      { from: 'Notch', subject: 'Gracias por jugar', preview: 'Me alegra ver gente construyendo mundos...', unread: true, time: 'ayer' },
      { from: 'Herobrine', subject: '...', preview: 'te estoy viendo', unread: true, time: 'ayer' },
      { from: 'Aldeano Vendedor', subject: 'Oferta esmeraldas', preview: '3x1 en trigo esta semana, no se lo pierda...', unread: false, time: 'lun' },
      { from: 'Tu Mamá', subject: 'Ordena tu pieza', preview: 'Te lo dije tres veces ya', unread: false, time: 'lun' },
      { from: 'Profesora', subject: 'Reunión apoderados', preview: 'Recordatorio: jueves 19:00 hrs', unread: false, time: 'dom' },
    ];

    const items = mails.map((m) => `
      <div style="
        display:flex; padding:12px 16px;
        border-bottom:1px solid #EEE;
        cursor: pointer;
      ">
        <div style="
          width: 4px; align-self: stretch;
          background: ${m.unread ? '#0066CC' : 'transparent'};
          border-radius: 2px; margin-right: 12px;
        "></div>
        <div style="flex:1; min-width:0;">
          <div style="display:flex; justify-content:space-between;">
            <div style="font-weight:${m.unread ? '700' : '400'}; color:#000; font-size:14px;">${m.from}</div>
            <div style="font-size:11px; color:#888;">${m.time}</div>
          </div>
          <div style="font-weight:${m.unread ? '600' : '400'}; color:#000; font-size:13px; margin-top:2px;">${m.subject}</div>
          <div style="font-size:12px; color:#666; margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${m.preview}</div>
        </div>
      </div>
    `).join('');

    return `
      <div style="background:#0066CC; color:white; padding:12px 16px; font-weight:600;">
        Mail · Bandeja
      </div>
      ${items}
    `;
  },
};

const APP_SAFARI = {
  id: 'safari',
  name: 'Safari',
  iconText: '🧭',
  iconBg: 'linear-gradient(135deg, #4FB3FF 0%, #0E83CD 100%)',
  bg: '#F2F2F7',
  render() {
    return `
      <div style="background:#FFFFFF; padding:8px 12px; border-bottom:1px solid #DDD; display:flex; gap:8px; align-items:center;">
        <div style="flex:1; background:#E8E8ED; border-radius:8px; padding:6px 10px; font-size:12px; color:#333;">
          🔒 mundofede.com
        </div>
      </div>
      <div style="padding: 20px 16px; background:#fff;">
        <div style="
          font-size: 32px; font-weight: 800;
          background: linear-gradient(90deg, #4FB3FF 0%, #0E83CD 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          margin-bottom: 4px;
        ">mundofede</div>
        <div style="color:#666; font-size:13px; margin-bottom:20px;">
          El mundo de Fede · v20.1
        </div>

        <div style="font-size:14px; color:#333; line-height:1.5; margin-bottom:16px;">
          Bienvenido al juego de Fede, hecho con su papá Edu.
          Más de 55.000 líneas de código, 152 bloques, 8 biomas
          y 3 dimensiones por explorar.
        </div>

        <div style="display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-top:20px;">
          <div style="background:#0066CC; color:white; padding:12px; border-radius:10px; text-align:center; font-size:12px; font-weight:600;">Jugar</div>
          <div style="background:#34C759; color:white; padding:12px; border-radius:10px; text-align:center; font-size:12px; font-weight:600;">Crear Mundo</div>
        </div>

        <div style="margin-top:24px; padding-top:16px; border-top:1px solid #EEE;">
          <div style="font-size:11px; color:#888; text-transform:uppercase; margin-bottom:8px;">Favoritos</div>
          ${[
            'github.com/fede-voxel',
            'minecraft.wiki',
            'three.js docs',
            'mundofede.com/tutorial',
          ].map((url) => `
            <div style="
              padding:8px 0; border-bottom:1px solid #F0F0F0;
              font-size:13px; color:#0066CC;
            ">${url}</div>
          `).join('')}
        </div>
      </div>
    `;
  },
};

export function getPhoneApps() {
  return [APP_TEAMS, APP_WAZE, APP_MAIL, APP_SAFARI];
}
